import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ProgressDots } from '@/components/ui/ProgressDots';
import { cn } from '@/components/ui/cn';
import { useQuizStore } from '../state';
import { QUESTIONS } from '../schema';

// ~15 seconds per question on average
const SECS_PER_Q = 15;

function timeEstimate(remaining: number, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const secs = remaining * SECS_PER_Q;
  if (secs <= SECS_PER_Q) return t('quiz.progress.timeLeftEnd');
  if (secs <= 75) return t('quiz.progress.timeLeftSoon');
  return t('quiz.progress.timeLeft', { minutes: Math.ceil(secs / 60) });
}

export interface QuizProgressProps {
  className?: string;
}

/**
 * Quiet Atlas progress line:
 *   ─── 03 / 08 · Climate ─────────────
 *
 * Reads currentStep from the store. Step number in mono, then a separator dot,
 * then the question topic in terra. Horizontal hairlines flank the text. Below,
 * the existing ProgressDots primitive provides the dot row (accessible too).
 *
 * Counts use the *visible* question slice for the current answer set — when
 * the career-intent gate hides the four follow-ups, the user sees a shorter
 * "03 / 16" instead of the structural "03 / 20". Position recalculates as
 * answers change so going back, switching the gate, then advancing keeps
 * the display honest.
 */
export function QuizProgress({ className }: QuizProgressProps) {
  const { t } = useTranslation();
  const currentStep = useQuizStore((s) => s.currentStep);
  const answers = useQuizStore((s) => s.answers);

  const { visiblePos, visibleTotal, question } = useMemo(() => {
    const visibleIdx: number[] = [];
    for (let i = 0; i < QUESTIONS.length; i++) {
      const q = QUESTIONS[i];
      if (!q.visible_when || q.visible_when(answers)) visibleIdx.push(i);
    }
    // Position of currentStep among visible questions. If currentStep is
    // hidden (transient state right after a gate flip), fall back to the
    // last visible step at or before currentStep so the counter never
    // shows something like "0 / 16".
    let pos = visibleIdx.indexOf(currentStep);
    if (pos === -1) {
      let scan = currentStep;
      while (scan > 0 && visibleIdx.indexOf(scan) === -1) scan--;
      pos = Math.max(0, visibleIdx.indexOf(scan));
    }
    return {
      visiblePos: pos,
      visibleTotal: visibleIdx.length,
      question: QUESTIONS[currentStep],
    };
  }, [currentStep, answers]);

  const total = visibleTotal;
  const stepNum = String(visiblePos + 1).padStart(2, '0');
  const totalNum = String(total).padStart(2, '0');
  const topic = question
    ? t(`quiz.progress.topic.${question.id}`, {
        defaultValue: t(question.label_key),
      })
    : '';
  const ariaLabel = t('quiz.progress.stepOf', { current: visiblePos + 1, total });
  const remaining = total - visiblePos;
  const timeHint = timeEstimate(remaining, t);

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div
        className="flex items-center gap-3 font-mono text-xs tracking-wider text-ink-500"
        aria-live="polite"
        aria-label={ariaLabel}
      >
        <span aria-hidden="true" className="h-px w-7 bg-ink-300" />
        <span className="font-medium text-ink-900 tabular-nums">
          {stepNum} / {totalNum}
        </span>
        <span aria-hidden="true" className="text-ink-300">
          ·
        </span>
        <span className="font-medium text-terra-600">{topic}</span>
        <span aria-hidden="true" className="h-px flex-1 bg-ink-100" />
        <span className="shrink-0 text-ink-400">{timeHint}</span>
      </div>
      <ProgressDots
        total={total}
        current={visiblePos}
        aria-label={ariaLabel}
        className="hidden"
      />
    </div>
  );
}
